import React from 'react';
import moment from 'moment';
import ReactPlayer from 'react-player';
import { useDidUpdateEffect } from '@odin-labs/components';
import { Player, PlayerProps } from 'components/player';
import { useTimeline } from 'components/timeline';
import { getTimeFromPosition } from 'components/timeline/utils';
import { JobsitePulseCamera } from 'containers/jobsite/tabs/jobsitePulse/data/types';
import { useJobsitePulse } from 'containers/jobsite/tabs/jobsitePulse/components/JobsitePulseProvider';
import { VideoFrame, VideoCaption, VideoUnavailable } from './video';
import { styles } from './styles';

const clampFraction = (value: number): number =>
  Math.min(Math.max(value, 0), 1);

export type FileMainCameraProps = {
  selectedCamera: JobsitePulseCamera;
};

/**
 * Returns the uploaded-video fallback camera (intended for use only in
 * non-prod envs, where jobsites have no Avigilon cameras).
 *
 * Avigilon cameras follow the timeline because their stream URL encodes the
 * timestamp. A file camera is a single static video, so we sync it ourselves.
 */
export function FileMainCamera({
  selectedCamera,
}: FileMainCameraProps): React.ReactElement {
  const { markerPosition, startTime, scale, timelineUnitCount } = useTimeline();
  const { shortTimeZone } = useJobsitePulse();

  const playerRef = React.useRef<ReactPlayer>(null);
  const [currentPlayTime, setCurrentPlayTime] = React.useState('');
  const [hasError, setHasError] = React.useState(false);

  // Reset transient state when switching file cameras.
  useDidUpdateEffect(() => {
    setCurrentPlayTime('');
    setHasError(false);
  }, [selectedCamera.url]);

  useDidUpdateEffect(() => {
    playerRef.current?.seekTo(clampFraction(markerPosition), 'fraction');
  }, [markerPosition]);

  const onProgressHandler: PlayerProps['onProgress'] = React.useCallback(
    ({ played }) => {
      const timestamp = getTimeFromPosition(
        played,
        startTime,
        scale,
        timelineUnitCount,
      );
      setCurrentPlayTime(
        `${moment(timestamp).format('hh:mm:ss a')} ${shortTimeZone || '...'}`,
      );
    },
    [startTime, scale, timelineUnitCount, shortTimeZone],
  );

  const onError: PlayerProps['onError'] = React.useCallback(() => {
    setHasError(true);
  }, []);

  const retry = React.useCallback((): void => {
    setHasError(false);
  }, []);

  return (
    <div className={styles.mainCamera}>
      <VideoFrame>
        <VideoCaption text={selectedCamera.name} suppressWhenNoText />
        <VideoCaption text={currentPlayTime} align="right" suppressWhenNoText />
        {hasError ? (
          <VideoUnavailable retry={retry} />
        ) : (
          <Player
            ref={playerRef}
            url={selectedCamera.url}
            value={{ fraction: clampFraction(markerPosition) }}
            className="odin-h-full odin-w-full"
            onError={onError}
            onProgress={onProgressHandler}
            enableFastForward
            customControls
            playing
            muted
          />
        )}
      </VideoFrame>
    </div>
  );
}
